import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@shared/entities/user';
import { UserFilter } from '@shared/entities/userFilter';
import { UserFiltersService } from '@shared/services/api/user-filter.service';
import { UserFilterURL, UserService } from '@shared/services/api/user.service';
import { UserMisalignedService } from '@shared/services/api/user-misaligned.service';
import { PartnerService } from '@shared/services/api/partner.service';
import { CustomerPartnerService } from '@shared/services/api/customer-partner.service';
import { AdminService } from '@shared/services/api/admin.service';
import { ThemeService } from '../services/theme.service';
import { Observable, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterOrSortDropdownType, FilterSelectedEventType, Roles } from '@shared/models/types';
import { trigger, style, transition, animate } from '@angular/animations';
import { TabItem } from '@shared/components/icam-tabs/icam-tabs.component';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class UserComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _userService = inject(UserService);
  private _partnerService = inject(PartnerService);
  private _customerPartnerService = inject(CustomerPartnerService);
  private _adminService = inject(AdminService);
  private _userFilterService = inject(UserFiltersService);
  private _userMisalignedService = inject(UserMisalignedService);
  public translateService = inject(TranslateService);

  users$: Observable<User[]>;
  partners$: Observable<User[]>;
  customerPartners$: Observable<User[]>;
  admins$: Observable<User[]>;
  userFilter$: Observable<UserFilter>;

  isLight = true;
  selectedTab = 0;
  showWelcomeMessageForm = false;

  tabs: TabItem[] = [
    { id: 0, label: 'USER.USER_LIST', icon: 'user-tab', needsTranslation: true },
    { id: 1, label: 'USER.PARTNER_LIST', icon: 'user-tab', needsTranslation: true },
    { id: 2, label: 'USER.CUSTOMER_PARTNER_LIST', icon: 'user-tab', needsTranslation: true },
    { id: 3, label: 'USER.ADMIN_LIST', icon: 'user-tab', needsTranslation: true }
  ];

  pagination: Record<number, any> = {
    0: { current: 1 },
    1: { current: 1 },
    2: { current: 1 },
    3: { current: 1 }
  };

  filters: Record<number, any> = {
    0: {},
    1: {},
    2: {},
    3: {}
  };

  private readonly TAB_BASE_FILTERS: Record<number, any> = {
    0: { roles: [Roles.CUSTOMER] },
    1: { role: Roles.PARTNER },
    2: { role: Roles.CUSTOMER_PARTNER },
    3: { role: Roles.ADMIN }
  };

  misalignedUsersCount = 0;
  hasMisalignedUsers = false;
  isMisalignedFilterActive = false;
  isUserMisalignedFilterActive = false;

  constructor() {
    this.users$ = this.setupPipe(this._userService, 0);
    this.partners$ = this.setupPipe(this._partnerService, 1);
    this.customerPartners$ = this.setupPipe(this._customerPartnerService, 2);
    this.admins$ = this.setupPipe(this._adminService, 3);
    this.userFilter$ = this._userFilterService.all$;
  }

  private setupPipe(service: any, tabId: number): Observable<User[]> {
    return service.allUsers$.pipe(
      switchMap((pagination: any) => {
        this.pagination[tabId] = {
          prev: pagination.prevPage,
          current: pagination.currentPage,
          next: pagination.nextPage,
          total: pagination.totalPage
        };
        this._cdr.markForCheck();
        return of(pagination.data);
      })
    );
  }

  ngOnInit() {
    this._themeService.refresh$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(() => this._themeService.isDarkTheme$)
      )
      .subscribe((isDarkTheme) => {
        this.isLight = !isDarkTheme;
        this._cdr.markForCheck();
      });

    this._userService.filters = this.TAB_BASE_FILTERS[0] as UserFilterURL;
    this._partnerService.initializePartners();
    this._customerPartnerService.initializeCustomerPartners();
    this._adminService.initializeAdmins();

    this.checkMisalignedUsers();
  }

  private getServiceByTab(tabId: number) {
    if (tabId === 1) return this._partnerService;
    if (tabId === 2) return this._customerPartnerService;
    if (tabId === 3) return this._adminService;
    return this._userService;
  }

  onTabChange(tab: TabItem) {
    this.switchTab(tab.id as number);
  }

  switchTab(index: number) {
    this.selectedTab = index;
    const baseFilters = this.TAB_BASE_FILTERS[index];
    const currentFilters = { ...this.filters[index], ...baseFilters };

    if (index === 0 && this.isUserMisalignedFilterActive) {
      currentFilters.status = 'MISALIGNED';
      this.isMisalignedFilterActive = true;
    } else {
      this.isMisalignedFilterActive = false;
    }

    const service = this.getServiceByTab(index);
    this._userFilterService.filters = currentFilters as UserFilterURL;
    service.filters = currentFilters as UserFilterURL;

    this._userFilterService
      .refresh(true)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._cdr.markForCheck());

    service
      .refresh(1)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._cdr.markForCheck());

    this.checkMisalignedUsers();
  }

  onSelectedFilter(filters: FilterSelectedEventType[]) {
    const filtersObj = filters.reduce((acc: any, filter) => {
      acc[filter.name] = filter.selectedValue;
      return acc;
    }, {});

    this.filters[this.selectedTab] = filtersObj;
    const tabFilters = { ...filtersObj, ...this.TAB_BASE_FILTERS[this.selectedTab] };

    const service = this.getServiceByTab(this.selectedTab);
    service.filters = tabFilters as UserFilterURL;
    this._userFilterService.filters = tabFilters as UserFilterURL;
  }

  onUserPage(page: number) {
    this.getServiceByTab(this.selectedTab).refresh(page).subscribe();
  }

  getFilterFields(filterData: any): FilterOrSortDropdownType[] {
    return Object.entries(filterData || {})
      .filter(([key, values]) => key !== 'orderBy' && Array.isArray(values))
      .map(([key, values]) => ({
        key: key,
        values: (values as any[]).map((value: string) => ({ id: value, value: value }))
      }));
  }

  getSortingOptions(filterData: any): FilterOrSortDropdownType[] {
    const orderBy = filterData?.orderBy;
    if (!orderBy) return [];
    return [
      {
        key: 'orderBy',
        values: (orderBy as any[]).map((value: string) => ({ id: value, value: value }))
      }
    ];
  }

  resetFilters() {
    this.filters[this.selectedTab] = {};
    const service = this.getServiceByTab(this.selectedTab);
    service.filters = this.TAB_BASE_FILTERS[this.selectedTab] as UserFilterURL;
    this._userFilterService.filters = {} as UserFilterURL;
  }

  private checkMisalignedUsers() {
    this._userMisalignedService.filters = this.TAB_BASE_FILTERS[this.selectedTab] as UserFilterURL;

    this._userMisalignedService
      .getMisalignedUsersCount()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((total) => {
        this.misalignedUsersCount = total;
        this.hasMisalignedUsers = total > 0;
        this._cdr.markForCheck();
      });
  }

  showMisalignedUsers() {
    const misalignedFilter = this.TAB_BASE_FILTERS[this.selectedTab];
    this._userMisalignedService.filters = misalignedFilter as UserFilterURL;

    this._userMisalignedService.getMisalignedUsersCount().subscribe((total) => {
      this.misalignedUsersCount = total;
      this.hasMisalignedUsers = total > 0;

      if (this.selectedTab !== 0) {
        this.switchTab(0);
      }

      this.isMisalignedFilterActive = !this.isMisalignedFilterActive;
      this.isUserMisalignedFilterActive = this.isMisalignedFilterActive;

      const filters = {
        ...this.filters[0],
        ...this.TAB_BASE_FILTERS[0]
      };

      if (this.isMisalignedFilterActive) {
        filters.status = 'MISALIGNED';
      } else {
        delete filters.status;
      }

      this._userFilterService.filters = filters as UserFilterURL;
      this._userService.filters = filters as UserFilterURL;
      this._userService.refresh(1).subscribe();
    });
  }

  toggleWelcomeMessageForm() {
    this.showWelcomeMessageForm = !this.showWelcomeMessageForm;
  }

  onCloseWelcomeMessageForm() {
    this.showWelcomeMessageForm = false;
  }
}
